import { useState, type FormEvent } from "react";

import { BrandMark } from "../components/BrandMark";
import { useAuth } from "./AuthProvider";
import { validateCredentials } from "./auth";

/**
 * Guard sign-in form. Pre-network validation gives inline field hints; the sign-in call itself
 * surfaces only the SAFE generic failure. On success the AuthProvider flips the gate to the guard
 * shell — nothing to navigate here.
 */
export function SignIn({ notice }: { readonly notice?: string | null } = {}) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Show/Hide toggle mirrors the native app (apps/mobile auth/password-visibility) — gloved
  // thumbs + long generated passwords make typos common in the field.
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const validation = validateCredentials({ email, password });
    if (!validation.ok) {
      setError(validation.reason);
      return;
    }

    setBusy(true);
    const result = await signIn({ email, password });
    setBusy(false);
    if (!result.ok) {
      setError(result.reason);
    }
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={handleSubmit} aria-labelledby="signin-title" noValidate>
        <header className="signin__brand">
          <BrandMark className="signin__mark" />
          <p className="signin__eyebrow">SecOps</p>
          <h1 id="signin-title" className="signin__title">
            Guard Web
          </h1>
          <p className="signin__subtitle">Guard sign-in</p>
        </header>

        {/* A dead invite link lands here: say what happened before the officer retypes a password
            they were never given. */}
        {notice ? (
          <p className="signin__error" role="alert">
            {notice}
          </p>
        ) : null}

        <label className="signin__field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
            required
          />
        </label>

        {/* The toggle button must NOT live inside the <label>: label activation forwards clicks
            to the control in browser-specific ways, which can double-fire the toggle. */}
        <div className="signin__field">
          <label htmlFor="signin-password">Password</label>
          <div className="signin__password">
            <input
              id="signin-password"
              type={passwordVisible ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
              required
            />
            <button
              className="signin__toggle"
              type="button"
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              aria-pressed={passwordVisible}
              onClick={() => setPasswordVisible((visible) => !visible)}
              disabled={busy}
            >
              {passwordVisible ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {error ? (
          <p className="signin__error" role="alert">
            {error}
          </p>
        ) : null}

        <button className="signin__submit" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
